import { cn } from "@/lib/utils";
import { LoadingSkeleton } from "./LoadingSkeleton";
import { Pagination } from "./Pagination";

/**
 * Column-driven responsive table. Desktop (>=md): real <table>. Mobile (<md): each row becomes
 * a card with label/value lines, so there is NO horizontal scroll and actions never go
 * off-screen. This is the single biggest responsive fix in the app.
 *
 * <DataTable
 *   columns={[
 *     { key: 'order', header: 'Order', cell: (r) => r.order_number, primary: true },
 *     { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
 *     { key: 'amount', header: 'Amount', cell: (r) => `₹${r.amount}`, align: 'right' },
 *     { key: 'actions', header: '', cell: (r) => <Button/>, isActions: true },
 *   ]}
 *   data={rows}
 *   rowKey={(r) => r.id}
 *   onRowClick={(r) => navigate(...)}
 *   pagination={{ page, pages, total, pageSize, onPageChange }}
 *   loading={loading}
 *   empty={<EmptyState .../>}
 * />
 *
 * Column shape:
 *  - key (string), header (node), cell(row) => node
 *  - align? 'left'|'right'|'center'
 *  - mobileLabel? (string, defaults to header) — label shown in the mobile card
 *  - primary? (bool) — becomes the mobile card title (no label)
 *  - hideOnMobile? (bool) — dropped from the mobile card
 *  - isActions? (bool) — rendered as full-width action cluster at the card foot on mobile
 */
const alignClass = (a) => (a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left");

export function DataTable({
  columns = [],
  data = [],
  rowKey,
  onRowClick,
  pagination,
  loading = false,
  empty = null,
  className,
  density = "normal", // 'normal' | 'compact'
}) {
  const keyOf = (row, i) => (rowKey ? rowKey(row, i) : i);
  const cellPad = density === "compact" ? "px-3 py-2" : "px-4 py-3";

  if (loading) {
    return <LoadingSkeleton variant="table" rows={8} cols={Math.max(columns.length, 3)} className={className} />;
  }

  if (!data.length) {
    return <div className={className}>{empty}</div>;
  }

  const mobileCols = columns.filter((c) => !c.hideOnMobile);

  return (
    <div className={cn("space-y-3", className)}>
      {/* Desktop table */}
      <div className="hidden md:block rounded-xl border bg-white overflow-x-auto">
        <table className="w-full caption-bottom text-sm">
          <thead>
            <tr className="border-b bg-slate-50">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={cn(
                    "px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap",
                    alignClass(c.align)
                  )}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {data.map((row, i) => (
              <tr
                key={keyOf(row, i)}
                onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                className={cn("transition-colors hover:bg-slate-50", onRowClick && "cursor-pointer")}
              >
                {columns.map((c) => (
                  <td key={c.key} className={cn(cellPad, "align-middle text-slate-700", alignClass(c.align))}>
                    {c.cell(row, i)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card-stack */}
      <div className="md:hidden space-y-2">
        {data.map((row, i) => {
          const primary = mobileCols.find((c) => c.primary);
          const actions = mobileCols.filter((c) => c.isActions);
          const fields = mobileCols.filter((c) => !c.primary && !c.isActions);
          return (
            <div
              key={keyOf(row, i)}
              onClick={onRowClick ? () => onRowClick(row, i) : undefined}
              className={cn(
                "rounded-xl border bg-white p-4 shadow-sm",
                onRowClick && "cursor-pointer active:bg-slate-50"
              )}
            >
              {primary && (
                <div className="mb-2 font-semibold text-slate-800 break-words">{primary.cell(row, i)}</div>
              )}
              <dl className="space-y-1.5">
                {fields.map((c) => (
                  <div key={c.key} className="flex items-start justify-between gap-3">
                    <dt className="text-xs font-medium text-slate-500 shrink-0">{c.mobileLabel ?? c.header}</dt>
                    <dd className="text-sm text-slate-700 text-right break-words min-w-0">{c.cell(row, i)}</dd>
                  </div>
                ))}
              </dl>
              {actions.length > 0 && (
                <div
                  className="mt-3 flex flex-col gap-2 border-t pt-3 [&_button]:w-full [&_a]:w-full"
                  onClick={(e) => e.stopPropagation()}
                >
                  {actions.map((c) => (
                    <div key={c.key}>{c.cell(row, i)}</div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {pagination && <Pagination {...pagination} />}
    </div>
  );
}

/**
 * Lower-level primitive for pages that need fully custom rendering: provide your own desktop
 * table node and a mobile card renderer. Handles only the responsive switch + loading/empty.
 *
 * <ResponsiveTable loading={loading} empty={<EmptyState/>}
 *   desktop={<table>...</table>}
 *   mobile={rows.map(r => <div key={r.id}>...</div>)} />
 */
export function ResponsiveTable({ desktop, mobile, loading = false, empty = null, hasData = true, className }) {
  if (loading) return <LoadingSkeleton variant="table" className={className} />;
  if (!hasData) return <div className={className}>{empty}</div>;
  return (
    <div className={className}>
      <div className="hidden md:block rounded-xl border bg-white overflow-x-auto">{desktop}</div>
      <div className="md:hidden space-y-2">{mobile}</div>
    </div>
  );
}

export default DataTable;
